<template>
    <transition mode="out-in">
        <div
            v-if="!initialLoaderAnimationLoopCompleted && $root.config.translations.results.initialLoaderLine1"
            class="flex flex-col justify-center items-center gap-y-1 box-border min-h-[calc(100dvh_-_11.875rem)] xLarge:min-h-[calc(100dvh_-_14.6875rem)]"
        >
            <div ref="resultsLoaderAnimation" class="mx-auto w-[11.125rem] h-[7.875rem]" />
            <div class="heading-xs font-medium text-center">
                <p>
                    {{ $root.config.translations.results.initialLoaderLine1 }}
                </p>
                <p v-if="$root.config.translations.results.initialLoaderLine1">
                    {{ $root.config.translations.results.initialLoaderLine2 }}
                </p>
            </div>
        </div>
        <div v-else>
            <h1 class="heading-m font-medium uppercase text-center xLarge:text-[1.625rem]">
                {{ $root.config.translations.results.heading }}
            </h1>
            <div class="mt-6 large:mx-auto large:max-w-[46.5625rem] xLarge:mt-8">
                <div v-if="products.length">
                    <ul class="flex flex-col gap-y-5">
                        <li
                            v-for="(product, index) in products"
                            :key="index"
                            class="p-5 border-[0.125rem] border-[#000000] bg-[#FFFFFF]"
                        >
                            <div class="flex largeMax:flex-col largeMax:gap-y-4 large:gap-x-8">
                                <div
                                    v-if="product.imageUrl && product.imageUrl2x"
                                    class="large:shrink-0 large:w-[18.1875rem]"
                                >
                                    <img
                                        class="aspect-square w-full h-full object-cover"
                                        :srcset="`${product.imageUrl} 1x,
                                                ${product.imageUrl2x} 2x`"
                                        :src="product.imageUrl"
                                        width="307"
                                        height="307"
                                        alt=""
                                        loading="eager"
                                    >
                                </div>
                                <div class="large:flex large:flex-col large:w-full">
                                    <div class="flex flex-col gap-y-[0.375rem] xLarge:gap-y-[0.625rem]">
                                        <h2 class="heading-s font-medium xLarge:text-[1.5rem]">
                                            {{ product.name }}
                                        </h2>
                                        <div
                                            v-if="product.description"
                                            class="wysiwyg body-s text-[#6C6C6C] xLarge:mt-[0.625rem]"
                                            v-html="product.description"
                                        />
                                    </div>
                                    <div class="heading-xs mt-[0.625rem] xLarge:text-[1.125rem]">
                                        <template v-if="product.price !== product.priceMin">{{ $root.config.translations.misc.from }}&nbsp;</template><span class="font-medium">{{ product.priceMin }}</span>
                                        <!-- @todo reviews -->
                                    </div>
                                    <div
                                        v-if="product.key_features.length"
                                        class="mt-3 large:mt-5"
                                    >
                                        <ul class="flex gap-y-[0.375rem] largeMax:flex-col large:flex-wrap large:gap-2">
                                            <li
                                                v-for="(key_feature, index) in product.key_features"
                                                :key="index"
                                                class="flex gap-x-[0.375rem] large:w-[calc(50%_-_0.25rem)]"
                                            >
                                                <div class="mt-0.5 w-[0.5625rem] xLarge:mt-[0.1875rem]">
                                                    <img
                                                        v-if="key_feature.image"
                                                        class="w-full"
                                                        :src="key_feature.image"
                                                        width="9"
                                                        height="9"
                                                        alt=""
                                                        loading="eager"
                                                    >
                                                    <svg
                                                        v-else
                                                        class="w-full"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                        role="presentation"
                                                        width="11"
                                                        height="8"
                                                        viewBox="0 0 11 8"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path d="M1 3.95752L4 6.95752L10 0.95752" stroke="currentColor"/>
                                                    </svg>
                                                </div>
                                                <p
                                                    v-if="key_feature.text"
                                                    class="body-xs"
                                                >
                                                    {{ key_feature.text }}
                                                </p>
                                            </li>
                                        </ul>
                                    </div>
                                    <div
                                        v-if="product.text"
                                        class="flex flex-wrap gap-x-2 mt-[1.125rem] large:mt-[1.625rem]"
                                    >
                                        <div
                                            v-if="product.textIcon && product.textIcon2x"
                                            class="xLarge:mt-px"
                                        >
                                            <img
                                                class="w-3 h-3 object-cover"
                                                :srcset="`${product.textIcon} 1x,
                                                        ${product.textIcon2x} 2x`"
                                                :src="product.textIcon"
                                                width="12"
                                                height="12"
                                                alt=""
                                                loading="eager"
                                            >
                                        </div>
                                        <div
                                            class="wysiwyg body-xs text-[#6C6C6C] xLarge:text-[0.75rem]"
                                            v-html="textHtml[product.id]"
                                        />
                                    </div>
                                    <div class="mt-[1.125rem] large:mt-auto large:pt-5">
                                        <div class="flex largeMax:flex-col largeMax:gap-2 large:gap-x-2">
                                            <button
                                                v-if="!$root.config.settings.dtc"
                                                class="button button--primary-on-light relative justify-center large:w-[calc(50%_-_0.25rem)]"
                                                :class="{ 'pointer-events-none': addProductLoading }"
                                                :disabled="!product.available"
                                                @click="addToCart(product.id)"
                                            >
                                                <div v-if="addProductLoading" class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-5">
                                                    <Loader class="border-[#FFFFFF] border-b-transparent" />
                                                </div>
                                                <template
                                                    v-if="!product.available"
                                                >
                                                    {{ $root.config.translations.misc.oos }}
                                                </template>
                                                <span
                                                    v-else
                                                    :class="{'invisible': addProductLoading}"
                                                >
                                                    {{ $root.config.translations.misc.addToCart }}
                                                </span>
                                            </button>
                                            <a
                                                :href="product.url"
                                                class="button button--secondary-on-light justify-center large:w-[calc(50%_-_0.25rem)]"
                                            >
                                                {{ $root.config.translations.misc.moreInfo }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    <div class="flex justify-end mt-7 large:mt-8">
                        <button
                            class="button button--tertiary-on-light button--has-chevron"
                            @click="restartQuiz"
                        >
                            {{ $root.config.translations.results.restartQuiz }}
                        </button>
                    </div>
                </div>
                <div
                    v-else
                    class="text-center"
                >
                    {{ $root.config.translations.results.noResults }}
                    <div class="mt-7">
                        <button
                            class="button button--tertiary-on-light button--has-chevron"
                            @click="restartQuiz"
                        >
                            {{ $root.config.translations.results.restartQuiz }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    import lottie from 'lottie-web';
    import resultsLoaderAnimation from '@/apps/quiz/results-loader.json';

    import Loader from '@/apps/quiz/components/Loader.vue';

    export default {
        components: {
            Loader
        },
        data () {
            return {
                allProducts: null,
                products: [],
                addProductLoading: false,
                initialLoaderAnimation: null,
                initialLoaderAnimationLoopCompleted: false,
                currentPage: 1,
                totalPages: 0,
                productsPerPage: 50,
                totalProducts: 0
            }
        },
        computed: {
            textHtml () {
                const obj = {};

                this.products.forEach(product => {
                    if (product.text) {
                        // Unescapes HTML entities
                        const tempEl = document.createElement('textarea');
                        tempEl.innerHTML = product.text;

                        // Parses as DOM so values can be changed
                        const parser = new DOMParser();
                        const htmlBody = parser.parseFromString(tempEl.value, 'text/html');

                        // Modified parsed HTML
                        obj[product.id] = htmlBody.body.innerHTML;
                    }
                });

                return obj;
            }
        },
        mounted () {
            this.initialLoaderAnimation = lottie.loadAnimation({
                container: this.$refs.resultsLoaderAnimation,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: resultsLoaderAnimation
            });

            // Ensures the `initialLoaderAnimation` loops through at least once
            let loopCount = 0;
            this.initialLoaderAnimation.addEventListener('loopComplete', () => {
                if (this.allProducts?.length) {
                    this.initialLoaderAnimationLoopCompleted = true;
                }

                loopCount++;
            });

            /**
             * If `allProducts` takes longer to populate than `initialLoaderAnimation`
             * length this ensures the animation does not have to complete another loop
             * to exit.
             */
            this.initialLoaderAnimation.addEventListener('enterFrame', event => {
                if (this.allProducts?.length && loopCount > 0) {
                    this.initialLoaderAnimationLoopCompleted = true;
                }
            });

            this.fetchAllData();

            /**
             * Load Klaviyo
             */
            if (this.$root.config.emailOptIn.customer?.email &&
                this.$root.config.emailOptIn.customer?.acceptsMarketing &&
                !window.klaviyo &&
                this.$root.config.emailOptIn.klaviyoAPIKey) {
                !function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();

                const script = document.createElement('script')
                
                script.type = 'text/javascript';
                script.async = true;
                script.src = `//static.klaviyo.com/onsite/js/${this.$root.config.emailOptIn.klaviyoAPIKey}/klaviyo.js`;

                document.body.appendChild(script);

                script.addEventListener('load', () => {
                    this.pushToKlaviyo();
                });
            } else if (this.$root.config.emailOptIn.customer?.email &&
                       this.$root.config.emailOptIn.customer?.acceptsMarketing &&
                       window.klaviyo) {
                // Assumed injected via Shopify App embed
                this.pushToKlaviyo();
            }
        },

        onAfterLeave () {
            this.initialLoaderAnimation?.destroy();
        },

        methods: {
            async fetchAllData() {
                this.error = null;
                this.loading = true;
                this.allProducts = [];
                try {
                    let currentPage = 1;
                    let totalPages = 1;
                    // Keep fetching until all pages are processed
                    while (currentPage <= totalPages) {
                        let data = await fetch(`/collections/quiz-products?sort_by=title-ascending&view=json&page=${currentPage}`, {
                            priority: 'high'
                        });
                        if (!data.ok) {
                            throw new Error(`Status ${data.status}`);
                        }
                        data = await data.json();

                        this.allProducts = [...this.allProducts, ...data.products];

                        this.totalProducts = data.totalProducts;
                        totalPages = Math.ceil(this.totalProducts / (this.productsPerPage + 1)); // Recalculate totalPages based on totalProducts

                        currentPage++; // Increment to fetch the next page
                    }
                } catch (error) {
                    this.error = error;
                } finally {
                    this.loading = false;
                    this.createQuizResults();
                }
            },
            async createQuizResults () {
                let filteredProducts = this.allProducts;

                // Filter by 'sleep'
                filteredProducts = filteredProducts.filter(product => product.quiz_sleep?.some(sleep => sleep.option === this.$route.params.sleep));

                // Filter by 'stress''
                filteredProducts = filteredProducts.filter(product => product.quiz_stress?.some(stress => stress.option === this.$route.params.stress));

                // Filter by 'live'
                filteredProducts = filteredProducts.filter(product => product.quiz_live?.some(live => live.option === this.$route.params.live));

                // Filter by 'diet'
                filteredProducts = filteredProducts.filter(product => product.quiz_diet?.some(diet => this.$route.params.diet.split(',').includes(diet.option)));

                // Filter by 'beauty'
                filteredProducts = filteredProducts.filter(product => product.quiz_beauty?.some(beauty => beauty.option === this.$route.params.beauty.split(',')[0]));

                // Filter by 'beauty:skin'
                if (this.$route.params.beauty.split(',').length > 1) {
                    const paramPrimary = this.$route.params.beauty.split(',')[0];

                    if (paramPrimary === 'skin') filteredProducts = filteredProducts.filter(product => product.quiz_beauty_skin?.some(beautySkin => beautySkin.option === this.$route.params.beauty.split(',')[1]));
                    if (paramPrimary === 'hair') filteredProducts = filteredProducts.filter(product => product.quiz_beauty_hair?.some(beautyHair => beautyHair.option === this.$route.params.beauty.split(',')[1]));
                    if (paramPrimary === 'nails') filteredProducts = filteredProducts.filter(product => product.quiz_beauty_nails?.some(beautyNails => beautyNails.option === this.$route.params.beauty.split(',')[1]));
                }

                // Filter by 'supplements'
                filteredProducts = filteredProducts.filter(product => product.quiz_supplements?.some(supplements => supplements.option === this.$route.params.supplements));

                // Filter by 'age'
                filteredProducts = filteredProducts.filter(product => product.quiz_age?.some(age => age.option === this.$route.params.age));

                this.products = filteredProducts;

                // NEW: persist variant SKUs so other steps (email opt-in) can read them
                await this.persistVariantSkus(filteredProducts);
            },

            restartQuiz () {
                this.$router.push({
                    name: 'Sleep',
                });
            },
            addToCart (id) {
                this.addProductLoading = true;
                const cartNotification = document.querySelector('cart-notification');
                const item = [{
                    'id': id,
                    'quantity': 1
                }]
                cartNotification.addToCart(item);
                window.addEventListener('cartChange', () => {
                    this.addProductLoading = false;
                })
                window.addEventListener('cartError', () => {
                    this.addProductLoading = false;
                })
            },
            async persistVariantSkus (products = []) {
                try {
                    // Resolve SKUs for items that don’t already expose sku/variants
                    const skuPromises = products.map(async (p) => {
                    if (p?.variantSku) return p.variantSku;
                    if (p?.sku) return p.sku;

                    // Fall back to fetching product JSON and matching the variant by varientId
                    if (p?.handle && p?.varientId) {
                        const res = await fetch(`/products/${p.handle}.js`, { priority: 'low' });
                        if (!res.ok) return null;
                        const prod = await res.json(); // { variants: [{ id, sku, ... }], ... }
                        const variantIdNum = Number(p.varientId);
                        const match = Array.isArray(prod?.variants)
                        ? prod.variants.find(v => Number(v?.id) === variantIdNum)
                        : null;
                        return match?.sku || null;
                    }

                    return null;
                    });

                    const skusRaw = await Promise.all(skuPromises);
                    const skus = Array.from(new Set(skusRaw.filter(Boolean)));

                    // Persist for the email-opt-in component to include in the Klaviyo event
                    sessionStorage.setItem('quiz_variant_skus', JSON.stringify(skus));
                } catch (_) {
                    // do nothing
                }
            },
            pushToKlaviyo () {
                window.klaviyo.identify({
                    'email': this.$root.config.emailOptIn.customer.email,
                    'first_name': this.$root.config.emailOptIn.customer.firstName
                }).then(() => {
                    const beautyInterest = this.$route.params.beauty.split(',')[0];
                    const beautyInterestSecondary = this.$route.params.beauty.split(',')[1];

                    // NEW: collect variant SKUs from the products currently rendered as results
                    // - Supports products with a single `sku`, a `variantSku`, or a `variants[]` array.
                    // - Filters out falsy values and de-duplicates.
                    const productVariantSkus = Array.from(new Set([
                    // from currently rendered products
                    ...(this.products || []).flatMap(p => {
                        if (Array.isArray(p.variants) && p.variants.length) return p.variants.map(v => v?.sku).filter(Boolean);
                        if (p?.variantSku) return [p.variantSku];
                        if (p?.sku) return [p.sku];
                        return [];
                        }),
                        // plus any persisted SKUs
                        ...(() => {
                            try {
                            return JSON.parse(sessionStorage.getItem('quiz_variant_skus') || '[]');
                            } catch { return []; }
                        })()
                    ]));

                    window.klaviyo.track('Quiz Results', {
                        'sleep': this.$route.params.sleep,
                        'stress': this.$route.params.stress,
                        'location': this.$route.params.live,
                        'diet': this.$route.params.diet,
                        'beauty_interest': beautyInterest,
                        ...(beautyInterest === 'skin' && { 'skin_improvement': beautyInterestSecondary }),
                        ...(beautyInterest === 'nails' && { 'nail_improvement': beautyInterestSecondary }),
                        ...(beautyInterest === 'hair' && { 'hair_improvement': beautyInterestSecondary }),
                        'supplements': this.$route.params.supplements,
                        'age': this.$route.params.age,
                        'variant_skus': productVariantSkus

                    });
                })
            }
        }
    };
</script>

<style scoped>
    .v-enter-active,
    .v-leave-active {
        transition: all 0.3s ease-out;
    }

    .v-enter-from,
    .v-leave-to {
        @apply opacity-0;
    }
</style>
